import React, { Suspense, useState, useEffect } from 'react'
import { ErrorBoundary } from './errors/ErrorBoundary'
import { ServiceType } from '../types'

interface ServiceWrapperProps {
  children: React.ReactNode
  serviceType: ServiceType
  serviceName: string
  apiBaseUrl?: string
}

interface LoadingFallbackProps {
  serviceName: string
}

const LoadingFallback: React.FC<LoadingFallbackProps> = ({ serviceName }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading {serviceName}</h2>
      <p className="text-gray-600">Please wait while we initialize the service...</p>
    </div>
  </div>
)

interface ServiceUnavailableFallbackProps {
  serviceName: string
  onRetry: () => void
}

const ServiceUnavailableFallback: React.FC<ServiceUnavailableFallbackProps> = ({
  serviceName,
  onRetry,
}) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center max-w-md mx-auto p-6">
      <div className="w-16 h-16 mx-auto mb-4 text-gray-400">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Service Unavailable</h2>
      <p className="text-gray-600 mb-4">
        {serviceName} is currently unavailable. This might be due to maintenance or a temporary
        issue.
      </p>
      <button
        onClick={onRetry}
        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
      >
        Try Again
      </button>
      <div className="mt-4">
        <a href="/" className="text-blue-600 hover:text-blue-800 text-sm underline">
          Return to Home
        </a>
      </div>
    </div>
  </div>
)

export const ServiceWrapper: React.FC<ServiceWrapperProps> = ({
  children,
  serviceType,
  serviceName,
  apiBaseUrl,
}) => {
  const resolvedApiBaseUrl = (apiBaseUrl ?? 'http://localhost:4000/api') as string
  const [isServiceAvailable, setIsServiceAvailable] = useState(true)

  // Inject API base URL into the service context
  useEffect(() => {
    // Set global API base URL for the service
    if (typeof window !== 'undefined') {
      const globalWindow = window as any
      globalWindow.__SERVICE_API_BASE_URL__ = resolvedApiBaseUrl
      globalWindow.__SERVICE_TYPE__ = serviceType
    }
  }, [resolvedApiBaseUrl, serviceType])

  const handleRetry = () => {
    setIsServiceAvailable(true)
  }

  if (!isServiceAvailable) {
    return <ServiceUnavailableFallback serviceName={serviceName} onRetry={handleRetry} />
  }

  return (
    <ErrorBoundary
      serviceType={serviceType}
      fallback={({ error, resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="w-16 h-16 mx-auto mb-4 text-red-400">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">
              {serviceName} encountered an error. Please try refreshing the page.
            </p>
            <div className="space-x-3">
              <button
                onClick={resetError}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Try Again
              </button>
              <a
                href="/"
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-md transition-colors inline-block"
              >
                Go Home
              </a>
            </div>
            {import.meta.env.DEV && error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-500">Error Details</summary>
                <pre className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded overflow-auto">
                  {error.message}
                  {error.stack && `\n\n${error.stack}`}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}
    >
      <Suspense fallback={<LoadingFallback serviceName={serviceName} />}>{children}</Suspense>
    </ErrorBoundary>
  )
}
